import { useState } from 'react';
import { 
  X, 
  Phone, 
  Mail, 
  MapPin,
  ExternalLink,
  Globe,
  Image,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { cn } from '../lib/utils';
import BrandingPanel, { parseBrandingData } from './BrandingPanel';
import { ADMIN_KEY, API_URL } from '../lib/config';

interface ClientRecord {
  id: string;
  created: string;
  updated: string;
  business_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  phone: string;
  activity: string;
  location: string;
  plan: string;
  addons: string;
  monthly_amount: number;
  total_oneshot: number;
  payment_status: string;
  paid_at: string;
  site_status: string;
  site_url: string;
  site_project: string;
  domain_name: string;
  domain_registered: boolean;
  logo_needed: boolean;
  logo_generated: boolean;
  logo_path: string;
  logo_url: string;
  logo_validated: boolean;
  logo_validated_at: string;
  has_logo: boolean;
  built_at: string;
  validated: boolean;
  validated_at: string;
  live_email_sent: boolean;
  branding_data: string | object | null;
  description: string;
  services: string | string[];
  style_choice: string;
  style_preference: string;
  comments: string;
  site_examples: string;
  site_error: string;
  promo_code: string;
  pricing: string;
  about_text: string;
  hours: string;
  stripe_payment_intent: string;
  stripe_subscription_id: string;
  completed_at: string;
  last_step: string;
  [key: string]: any;
}

interface ClientDetailProps {
  client: ClientRecord;
  onClose: () => void;
}

const addonLabels: Record<string, string> = {
  logo: '🎨 Logo',
  blog: '📝 Blog',
  form: '📋 Formulaire',
  domain: '🌐 Domaine',
  gmb: '📍 GMB',
  gmb_creation: '📍 Création GMB',
  gmb_optim: '📍 Optim GMB',
  visibility_standard: '🚀 Visibilité',
};

// Grille tarifaire pour le détail commande — par plan
const PRICING_ONESHOT: Record<string, { label: string; price: number; unit: string }> = {
  site: { label: 'Site web', price: 349, unit: '' },
  logo: { label: 'Logo professionnel', price: 49, unit: '' },
  domain: { label: 'Nom de domaine (1 an)', price: 49, unit: '' },
  gmb_creation: { label: 'Création fiche Google', price: 79, unit: '' },
  seo_setup: { label: 'SEO Setup', price: 99, unit: '' },
};

const PRICING_SAAS: Record<string, { label: string; price: number; unit: string }> = {
  site: { label: 'Abonnement site web', price: 39, unit: '/mois' },
  logo: { label: 'Logo professionnel', price: 49, unit: ' (une fois)' },
  domain: { label: 'Hébergement + domaine personnalisé', price: 5, unit: '/mois' },
  gmb_creation: { label: 'Création fiche Google', price: 79, unit: ' (une fois)' },
  seo_suivi: { label: 'SEO Suivi', price: 29, unit: '/mois' },
  maintenance: { label: 'Maintenance', price: 19, unit: '/mois' },
};

const PLAN_LABELS: Record<string, string> = {
  oneshot: '🔑 Clé en main (One-shot)',
  saas: '📦 Abonnement SaaS',
};

function computeOrderDetail(plan: string, addons: string[]) {
  const isOneshot = plan === 'oneshot';
  const grid = isOneshot ? PRICING_ONESHOT : PRICING_SAAS;
  const lines: { label: string; price: number; unit: string; monthly: boolean }[] = [];
  
  // Site is always included
  const siteLine = grid.site;
  lines.push({ ...siteLine, monthly: !isOneshot });
  
  for (const addon of addons) {
    const addonKey = addon.replace('-', '_');
    const entry = grid[addonKey];
    if (entry) {
      const isMonthly = entry.unit.includes('/mois');
      lines.push({ ...entry, monthly: isMonthly });
    }
  }
  
  const totalOneshot = lines.filter(l => !l.monthly).reduce((sum, l) => sum + l.price, 0);
  const totalMonthly = lines.filter(l => l.monthly).reduce((sum, l) => sum + l.price, 0);
  
  return { lines, totalOneshot, totalMonthly, isOneshot };
}

function parseLogoPath(logoPath: string | null | undefined): Record<string, string> | null {
  if (!logoPath) return null;
  try {
    return JSON.parse(logoPath);
  } catch {
    return null;
  }
}

function getLogoUrls(siteUrl: string | null | undefined, logoData: Record<string, string> | null): string[] {
  if (!logoData || !siteUrl) return [];
  const urls: string[] = [];
  const baseUrl = siteUrl.replace(/\/$/, '');
  for (const [, filename] of Object.entries(logoData)) {
    if (filename) {
      urls.push(`${baseUrl}/assets/${filename}`);
    }
  }
  return urls;
}

function parseAddons(addonsStr: string | string[] | null | undefined): string[] {
  if (!addonsStr) return [];
  if (Array.isArray(addonsStr)) return addonsStr;
  try { return JSON.parse(addonsStr); } catch { return []; }
}

function formatDateFr(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch { return dateStr; }
}

function parseServices(services: string | string[] | null | undefined): string[] {
  if (!services) return [];
  if (Array.isArray(services)) return services;
  try { return JSON.parse(services); } catch { return [services]; }
}

const SITE_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'En attente', color: 'text-gray-600', bg: 'bg-gray-100' },
  building: { label: 'Construction', color: 'text-blue-700', bg: 'bg-blue-100' },
  waiting_client: { label: 'Attente client', color: 'text-amber-700', bg: 'bg-amber-100' },
  client_responded: { label: 'Client a répondu', color: 'text-indigo-700', bg: 'bg-indigo-100' },
  built: { label: 'Construit', color: 'text-emerald-700', bg: 'bg-emerald-100' },
  built_auto: { label: 'Construit (auto)', color: 'text-emerald-700', bg: 'bg-emerald-100' },
  live: { label: 'En ligne', color: 'text-green-700', bg: 'bg-green-100' },
  error: { label: 'Erreur', color: 'text-red-700', bg: 'bg-red-100' },
};

function SiteStatusBadge({ status }: { status: string | null | undefined }) {
  const config = SITE_STATUS_CONFIG[status || ''] || { label: status || 'Inconnu', color: 'text-gray-600', bg: 'bg-gray-100' };
  return (
    <span className={cn('inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium', config.bg, config.color)}>
      {config.label}
    </span>
  );
}

// Parse description Q&A format
function QASection({ description }: { description: string }) {
  if (!description) return null;
  
  // Split by bold markers **question**
  const parts = description.split(/\*\*(.*?)\*\*/g);
  
  if (parts.length <= 1) {
    // No bold markers, just render as text
    return <p className="text-sm text-gray-600 whitespace-pre-wrap">{description}</p>;
  }
  
  const items: { question: string; answer: string }[] = [];
  for (let i = 1; i < parts.length; i += 2) {
    items.push({
      question: parts[i] || '',
      answer: (parts[i + 1] || '').trim(),
    });
  }
  
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm font-semibold text-gray-800 mb-1">{item.question}</p>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.answer}</p>
        </div>
      ))}
    </div>
  );
}

// Pipeline timeline
const PIPELINE_STEPS = [
  { key: 'payment', label: 'Paiement', icon: '💳' },
  { key: 'branding', label: 'Analyse branding', icon: '🎨' },
  { key: 'questions', label: 'Questions', icon: '❓' },
  { key: 'generation', label: 'Génération', icon: '🔨' },
  { key: 'deployment', label: 'Déploiement', icon: '🚀' },
  { key: 'review', label: 'Validation', icon: '👀' },
  { key: 'live', label: 'En ligne', icon: '✅' },
];

function getStepStatus(client: ClientRecord, stepKey: string): 'done' | 'active' | 'pending' | 'error' {
  switch (stepKey) {
    case 'payment':
      return client.payment_status === 'paid' ? 'done' : 'pending';
    case 'branding':
      if (client.site_status === 'error') return 'error';
      if (['building', 'built', 'built_auto', 'live', 'waiting_client', 'client_responded'].includes(client.site_status)) return 'done';
      if (client.payment_status === 'paid' && (!client.site_status || client.site_status === 'pending')) return 'active';
      return 'pending';
    case 'questions':
      if (client.site_status === 'waiting_client') return 'active';
      if (['client_responded', 'building', 'built', 'built_auto', 'live'].includes(client.site_status)) return 'done';
      return 'pending';
    case 'generation':
      if (client.site_status === 'error') return 'error';
      if (['built', 'built_auto', 'live'].includes(client.site_status)) return 'done';
      if (client.site_status === 'building' || client.site_status === 'client_responded') return 'active';
      return 'pending';
    case 'deployment':
      if (['built', 'built_auto', 'live'].includes(client.site_status) && client.site_url) return 'done';
      if (client.site_status === 'building') return 'active';
      return 'pending';
    case 'review':
      if (client.validated) return 'done';
      if (client.site_url && ['built', 'built_auto'].includes(client.site_status)) return 'active';
      return 'pending';
    case 'live':
      if (client.validated && client.live_email_sent) return 'done';
      if (client.validated) return 'active';
      return 'pending';
    default:
      return 'pending';
  }
}

function Section({ title, icon, children, defaultOpen = true }: { title: string; icon: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button 
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span>{icon}</span>
        <span className="text-sm font-semibold text-gray-800 flex-1">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

export default function ClientDetail({ client, onClose }: ClientDetailProps) {
  const addons = parseAddons(client.addons);
  const services = parseServices(client.services);
  const brandingData = parseBrandingData(client.branding_data);
  const orderDetail = computeOrderDetail(client.plan, addons);
  const logoData = parseLogoPath(client.logo_path);
  const logoUrls = getLogoUrls(client.site_url, logoData);
  const priceWarning = client.monthly_amount > 500 || client.total_oneshot > 2000;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 animate-fadeIn" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white h-full overflow-hidden flex flex-col animate-slideIn shadow-2xl">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center text-green-700 font-bold text-lg flex-shrink-0">
                  {(client.business_name || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">
                    {client.business_name || 'Client'}
                  </h2>
                  <p className="text-sm text-gray-500 truncate">
                    {client.contact_name} · {client.activity} · {client.location}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  {client.plan === 'oneshot' ? '🔑 Clé en main' : '📦 SaaS'}
                </span>
                {client.monthly_amount > 0 && (
                  <span className="text-sm font-medium text-gray-700">
                    {client.monthly_amount}€/mois
                  </span>
                )}
                {client.total_oneshot > 0 && (
                  <span className="text-sm font-medium text-gray-700">
                    {client.total_oneshot}€ one-shot
                  </span>
                )}
                <SiteStatusBadge status={client.site_status} />
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 -m-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Quick contact */}
          <div className="flex items-center gap-2 mt-4">
            {client.contact_email && (
              <a
                href={`mailto:${client.contact_email}`}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4" />
                {client.contact_email}
              </a>
            )}
            {(client.contact_phone || client.phone) && (
              <a
                href={`tel:${client.contact_phone || client.phone}`}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4" />
                {client.contact_phone || client.phone}
              </a>
            )}
            {client.site_url && (
              <a
                href={client.site_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                <Globe className="w-4 h-4" />
                Voir le site
              </a>
            )}
          </div>
        </div>
        
        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
          
          {/* Détail commande */}
          <Section title="Détail commande" icon="🧾" defaultOpen={true}>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">N° de commande</span>
                <span className="font-mono text-gray-800 text-xs">{client.id}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Plan</span>
                <span className="font-medium text-gray-800">{PLAN_LABELS[client.plan] || client.plan || '—'}</span>
              </div>
              
              <div className="border-t border-gray-100 pt-2 space-y-1.5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Détail tarifaire</p>
                {orderDetail.lines.map((line, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-gray-700">{line.label}</span>
                    <span className="text-gray-800 font-medium">
                      {line.price > 0 ? `${line.price}€${line.unit}` : 'Inclus'}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="border-t border-gray-200 pt-2 flex items-center justify-between">
                <div>
                  {orderDetail.isOneshot ? (
                    <p className="text-gray-800 font-semibold">Total : {orderDetail.totalOneshot + orderDetail.totalMonthly}€</p>
                  ) : (
                    <>
                      <p className="text-gray-800 font-semibold">{orderDetail.totalMonthly}€/mois</p>
                      {orderDetail.totalOneshot > 0 && (
                        <p className="text-gray-600">+ {orderDetail.totalOneshot}€ (une fois)</p>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {/* Comparaison avec les valeurs stockées */}
              {(client.monthly_amount > 0 || client.total_oneshot > 0) && (
                <div className="border-t border-gray-100 pt-2">
                  <p className="text-xs text-gray-400">Valeurs enregistrées : {client.total_oneshot}€ one-shot / {client.monthly_amount}€/mois</p>
                </div>
              )}
              
              {/* Warning prix aberrant */}
              {priceWarning && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <span className="text-lg">⚠️</span>
                  <div>
                    <p className="text-amber-800 font-semibold text-xs">Prix potentiellement incohérent</p>
                    <p className="text-amber-700 text-xs mt-0.5">
                      {client.monthly_amount > 500 && `${client.monthly_amount}€/mois semble élevé pour un artisan/TPE. `}
                      {client.total_oneshot > 2000 && `${client.total_oneshot}€ one-shot semble élevé. `}
                      Vérifiez le détail ci-dessus.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Section>

          {/* Addons */}
          {addons.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {addons.map(addon => (
                <span key={addon} className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium">
                  {addonLabels[addon] || addon}
                </span>
              ))}
            </div>
          )}

          {/* Questionnaire initial */}
          <Section title="Questionnaire initial" icon="📋" defaultOpen={false}>
            <div className="space-y-3 text-sm">
              {client.activity && (
                <div>
                  <span className="text-gray-500 text-xs block mb-0.5">Activité</span>
                  <span className="text-gray-800">{client.activity}</span>
                </div>
              )}
              {services.length > 0 && (
                <div>
                  <span className="text-gray-500 text-xs block mb-1">Services</span>
                  <div className="flex flex-wrap gap-1.5">
                    {services.map((s, i) => (
                      <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {client.location && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-gray-800">{client.location}</span>
                </div>
              )}
              {client.style_choice && (
                <div>
                  <span className="text-gray-500 text-xs block mb-0.5">Style choisi</span>
                  <span className="text-gray-800">{client.style_choice}</span>
                </div>
              )}
              {client.style_preference && (
                <div>
                  <span className="text-gray-500 text-xs block mb-0.5">Préférence de style</span>
                  <span className="text-gray-800">{client.style_preference}</span>
                </div>
              )}
              {client.site_examples && (
                <div>
                  <span className="text-gray-500 text-xs block mb-0.5">Exemples de sites</span>
                  <p className="text-gray-800 whitespace-pre-wrap">{client.site_examples}</p>
                </div>
              )}
              {client.comments && (
                <div>
                  <span className="text-gray-500 text-xs block mb-0.5">Commentaires</span>
                  <p className="text-gray-800 whitespace-pre-wrap">{client.comments}</p>
                </div>
              )}
            </div>
          </Section>

          {/* Réponses aux questions */}
          {client.description && (
            <Section title="Réponses aux questions" icon="💬" defaultOpen={false}>
              <QASection description={client.description} />
            </Section>
          )}

          {/* Brief créatif */}
          {brandingData && (
            <Section title="Brief créatif (Nina)" icon="🎨" defaultOpen={false}>
              <BrandingPanel data={brandingData} />
            </Section>
          )}

          {/* Site */}
          <Section title="Site" icon="🌐">
            <div className="space-y-4">
              {/* Status pipeline */}
              <div className="flex items-center gap-1 flex-wrap">
                {PIPELINE_STEPS.map((step, i) => {
                  const status = getStepStatus(client, step.key);
                  return (
                    <div key={step.key} className="flex items-center">
                      <div className={cn(
                        'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                        status === 'done' && 'bg-green-50 text-green-700',
                        status === 'active' && 'bg-orange-50 text-orange-700 animate-pulse',
                        status === 'pending' && 'bg-gray-50 text-gray-400',
                        status === 'error' && 'bg-red-50 text-red-600',
                      )}>
                        <span>{step.icon}</span>
                        <span className="hidden md:inline">{step.label}</span>
                      </div>
                      {i < PIPELINE_STEPS.length - 1 && (
                        <div className={cn('w-3 h-0.5 mx-0.5', status === 'done' ? 'bg-green-300' : 'bg-gray-200')} />
                      )}
                    </div>
                  );
                })}
              </div>

              {client.site_url && (
                <div>
                  <span className="text-gray-500 text-xs block mb-1">URL du site</span>
                  <a href={client.site_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline flex items-center gap-1">
                    {client.site_url} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}

              {client.domain_name && (
                <div>
                  <span className="text-gray-500 text-xs block mb-1">Domaine</span>
                  <span className="text-sm text-gray-700">
                    {client.domain_name} {client.domain_registered ? '✅ Enregistré' : '⏳ En attente'}
                  </span>
                </div>
              )}

              {client.site_project && (
                <div>
                  <span className="text-gray-500 text-xs block mb-1">Projet Cloudflare Pages</span>
                  <span className="text-sm text-gray-700 font-mono">{client.site_project}</span>
                </div>
              )}

              {/* Iframe preview */}
              {client.site_url && (
                <div>
                  <span className="text-gray-500 text-xs block mb-1">Aperçu</span>
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white" style={{ height: 300 }}>
                    <iframe 
                      src={client.site_url} 
                      className="w-full h-full" 
                      title="Aperçu du site"
                      sandbox="allow-scripts allow-same-origin"
                    />
                  </div>
                </div>
              )}
            </div>
          </Section>

          {/* Logo */}
          <Section title="Logo" icon="🖼️" defaultOpen={!!client.logo_url}>
            <div className="space-y-3 text-sm">
              {/* Logo principal via logo_url */}
              {client.logo_url && (
                <div>
                  <div className="flex items-center gap-4">
                    <a href={client.logo_url} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl border border-gray-200 p-3 flex items-center justify-center cursor-pointer hover:border-orange-300 hover:shadow-md transition-all" style={{width: 120, height: 120}}>
                      <img src={client.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                    </a>
                    <a href={client.logo_url} target="_blank" rel="noopener noreferrer" className="bg-gray-800 rounded-xl p-3 flex items-center justify-center cursor-pointer hover:shadow-md transition-all" style={{width: 120, height: 120}}>
                      <img src={client.logo_url} alt="Logo fond sombre" className="max-w-full max-h-full object-contain" />
                    </a>
                  </div>
                  {client.logo_validated ? (
                    <div className="mt-3 flex items-center gap-2">
                      <span className="px-3 py-1.5 text-xs bg-green-50 text-green-600 rounded-lg font-medium">✅ Logo validé</span>
                      <span className="text-xs text-gray-400">{client.logo_validated_at ? `le ${new Date(client.logo_validated_at).toLocaleDateString('fr-FR')}` : ''}</span>
                    </div>
                  ) : (
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!confirm('Valider ce logo ? Le client recevra un email avec son logo en téléchargement.')) return;
                          try {
                            const res = await fetch(`${API_URL}/admin/validate-logo`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ submissionId: client.id, adminKey: ADMIN_KEY })
                            });
                            const data = await res.json();
                            if (data.ok) {
                              alert(data.emailSent ? '✅ Logo validé ! Email envoyé au client avec lien de téléchargement.' : '✅ Logo validé (email non envoyé)');
                              window.location.reload();
                            } else {
                              alert('❌ Erreur: ' + (data.error || 'Inconnu'));
                            }
                          } catch (err: any) { alert('Erreur: ' + err.message); }
                        }}
                        className="px-3 py-1.5 text-xs bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors font-medium"
                      >
                        ✅ Valider le logo
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm('Regénérer le logo ? L\'ancien sera remplacé.')) {
                            alert('🔄 Regénération en cours... Le nouveau logo apparaîtra dans quelques minutes.');
                            fetch(`${API_URL}/admin/regenerate-logo`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ submissionId: client.id, adminKey: ADMIN_KEY })
                            });
                          }
                        }}
                        className="px-3 py-1.5 text-xs bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition-colors"
                      >
                        🔄 Regénérer
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {!client.logo_url && (
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-gray-500 text-xs">Logo nécessaire</span>
                    <p className="font-medium">{client.logo_needed ? '✅ Oui' : '❌ Non'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs">Logo généré</span>
                    <p className="font-medium">{client.logo_generated ? '✅ Oui' : '⏳ Non'}</p>
                  </div>
                </div>
              )}
              
              {/* Fallback ancien système */}
              {!client.logo_url && logoUrls.length > 0 && (
                <div>
                  <span className="text-gray-500 text-xs block mb-2">Aperçu logo</span>
                  <div className="flex flex-wrap gap-3">
                    {logoUrls.map((url, i) => (
                      <div key={i} className="border border-gray-200 rounded-lg p-2 bg-white">
                        <img src={url} alt={`Logo ${i + 1}`} className="max-h-20 max-w-[160px] object-contain"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {!client.logo_url && client.logo_path && logoUrls.length === 0 && (
                <div>
                  <span className="text-gray-500 text-xs block mb-1">Fichier logo</span>
                  <div className="flex items-center gap-2">
                    <Image className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700 font-mono text-xs">{client.logo_path}</span>
                  </div>
                  {client.logo_path.startsWith('http') && (
                    <img src={client.logo_path} alt="Logo" className="mt-2 max-h-24 rounded-lg border border-gray-200" />
                  )}
                </div>
              )}
            </div>
          </Section>

          {/* Historique */}
          <Section title="Historique" icon="📅">
            <div className="space-y-3">
              {[
                { label: 'Paiement', date: client.paid_at, icon: '💳' },
                { label: 'Construction', date: client.built_at, icon: '🔨' },
                { label: 'Validation', date: client.validated_at, icon: '✅' },
                { label: 'Email live envoyé', date: client.live_email_sent ? (client.validated_at || 'Oui') : null, icon: '📧' },
                { label: 'Terminé', date: client.completed_at, icon: '🎉' },
              ].filter(e => e.date).map((event, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center text-sm flex-shrink-0">
                    {event.icon}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{event.label}</p>
                    <p className="text-xs text-gray-500">
                      {typeof event.date === 'string' && event.date !== 'Oui' ? formatDateFr(event.date) : 'Oui'}
                    </p>
                  </div>
                </div>
              ))}

              {!client.paid_at && !client.built_at && !client.validated_at && (
                <p className="text-sm text-gray-400 italic">Aucun événement enregistré</p>
              )}
            </div>

            {/* Site error / pipeline status - parse JSON intelligently */}
            {client.site_error && (() => {
              try {
                const parsed = JSON.parse(client.site_error);
                
                // Type: precision_questions → affichage questions
                if (parsed.type === 'precision_questions') {
                  return (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                      <p className="text-xs font-semibold text-amber-700 mb-1">📋 Questions envoyées au client</p>
                      <ul className="text-xs text-amber-600 space-y-1 mt-1">
                        {parsed.questions.map((q: any, i: number) => (
                          <li key={i}>• {q.question}</li>
                        ))}
                      </ul>
                      <p className="text-xs text-amber-500 mt-2">Envoyées le {new Date(parsed.asked_at).toLocaleString('fr-FR')}</p>
                    </div>
                  );
                }
                
                // Type: pipeline progress (steps array) → affichage progression
                if (parsed.steps && Array.isArray(parsed.steps)) {
                  const statusIcons: Record<string, string> = { done: '✅', in_progress: '⏳', pending: '⏸️', error: '❌' };
                  return (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                      <p className="text-xs font-semibold text-blue-700 mb-2">🔄 Pipeline en cours</p>
                      <div className="space-y-1">
                        {parsed.steps.map((s: any, i: number) => (
                          <div key={i} className="text-xs text-blue-600 flex items-center gap-1">
                            <span>{statusIcons[s.status] || '⏸️'}</span>
                            <span className={s.status === 'in_progress' ? 'font-semibold' : ''}>{s.label}</span>
                          </div>
                        ))}
                      </div>
                      {parsed.error && <p className="text-xs text-red-500 mt-2">⚠️ {parsed.error}</p>}
                      <p className="text-xs text-blue-400 mt-2">MàJ {new Date(parsed.updated_at).toLocaleString('fr-FR')}</p>
                    </div>
                  );
                }
              } catch {}
              // Fallback: vrai message d'erreur texte
              return (
                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-xs font-semibold text-red-700 mb-1">Erreur site</p>
                  <p className="text-xs text-red-600 whitespace-pre-wrap font-mono">{client.site_error}</p>
                </div>
              );
            })()}
          </Section>
        </div>
      </div>
    </div>
  );
}
